import { api, apiClient } from './client';
import { uploadDirect } from '@/api/direct-upload.api';

export interface PlanLimits {
  maxBranches: number;
  maxUsersPerBranch: number;
  maxRoomsPerBranch: number;
  hasBookingApp: boolean;
}

export interface SubscriptionPackage {
  id: number;
  name: string;
  description: string;
  price: number;
  monthlyPrice: number;
  yearlyPrice: number;
  interval: string;
  durationDays: number;
  isTrial: boolean;
  features: string;
  maxBranches: number;
  maxUsersPerBranch: number;
  maxRoomsPerBranch: number;
  hasBookingApp: boolean;
}

export interface ClinicSubscription {
  id: number;
  clinicId: string;
  packageId: number;
  status: string;
  startDate: string;
  endDate: string;
  additionalBranches: number;
  additionalBranchFee: number;
  monthlyPrice: number;
  yearlyPrice: number;
  billingType: 'monthly' | 'yearly' | null;
  customPrice: number;
  yearlyIncreasePercent: number;
  package: SubscriptionPackage;
}

export interface SubscriptionStatus {
  hasSubscription: boolean;
  isActive: boolean;
  isExpired: boolean;
  isInGracePeriod: boolean;
  isBlocked: boolean;
  daysUntilExpiration: number;
  daysUntilGraceEnds: number;
  showWarning: boolean;
  status: string;
  endDate?: string;
  graceEndsAt?: string;
  packageName?: string;
}

export const subscriptionApi = {
  getPlanLimits: () =>
    api.get<{ data: PlanLimits }>(`/subscriptions/clinic/limits`),

  checkBookingAccess: () =>
    api.get<{ data: { hasBookingApp: boolean } }>(
      `/subscriptions/clinic/booking-access`,
    ),

  getClinicSubscription: () =>
    api.get<{ data: ClinicSubscription }>(`/subscriptions/clinic`),

  getSubscriptionStatus: () =>
    api.get<SubscriptionStatus>(`/subscriptions/clinic/status`),

  getAllPackages: () =>
    api.get<{ data: SubscriptionPackage[] }>('/subscriptions/packages'),
};

// ── Renewal Requests ──────────────────────────────────────────────────────

export interface RenewalRequest {
  id: number;
  clinicId: string;
  userId: string;
  paymentMethod: string;
  amount?: number;
  transactionReference?: string;
  screenshotKey?: string;
  notes?: string;
  status: 'pending' | 'approved' | 'rejected' | 'awaiting_payment';
  rejectionReason?: string;
  remainingAmount?: number;
  extensionGranted: number;
  createdAt: string;
  clinicName?: string;
  userName?: string;
  userEmail?: string;
  screenshotUrl?: string;
}

export const renewalRequestApi = {
  submit: async (data: {
    paymentMethod: string;
    amount?: number;
    transactionReference?: string;
    notes?: string;
    screenshot?: File;
    requestedPackageId?: number;
    requestedAdditionalBranches?: number;
    requestedBillingType: 'monthly' | 'yearly';
  }) => {
    const uploadKey = data.screenshot
      ? await uploadDirect(data.screenshot, 'payment-screenshot')
      : undefined;
    return apiClient
      .post<RenewalRequest>('/renewal-requests', {
        paymentMethod: data.paymentMethod,
        amount: data.amount || undefined,
        transactionReference: data.transactionReference || undefined,
        notes: data.notes || undefined,
        uploadKey,
        requestedPackageId: data.requestedPackageId || undefined,
        requestedAdditionalBranches: data.requestedAdditionalBranches,
        requestedBillingType: data.requestedBillingType,
      })
      .then((res) => res.data);
  },

  updatePayment: async (
    id: number,
    data: {
      amount?: number;
      transactionReference?: string;
      notes?: string;
      screenshot?: File;
    },
  ) => {
    const uploadKey = data.screenshot
      ? await uploadDirect(data.screenshot, 'payment-screenshot')
      : undefined;
    return apiClient
      .post<RenewalRequest>(`/renewal-requests/${id}/update-payment`, {
        amount: data.amount || undefined,
        transactionReference: data.transactionReference || undefined,
        notes: data.notes || undefined,
        uploadKey,
      })
      .then((res) => res.data);
  },

  getMyRequests: () => api.get<RenewalRequest[]>('/renewal-requests'),
};
